import React, { useState, useEffect } from "react";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { X, Calendar, User, Flag, MessageCircle, Paperclip, CheckSquare, Tag, AlertCircle, Loader2, Send, Plus, Trash2, Check, Lock, HandHelping, BookmarkPlus, FolderOpen, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AssistanceModal from "@/components/task/AssistanceModal";

const PRIORITY_OPTIONS = [
    { value: "low", label: "Scăzut", color: "#6B7280" },
    { value: "medium", label: "Mediu", color: "#F59E0B" },
    { value: "high", label: "Ridicat", color: "#F97316" },
    { value: "critical", label: "Critic", color: "#EF4444" },
];

function Section({ title, icon, children }) {
    return (
        <div className="border-b border-[#F1F3F5] pb-5 mb-5">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[#9CA3AF]">{icon}</span>
                <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-widest">{title}</h3>
            </div>
            {children}
        </div>
    );
}

export default function TaskDetailsDrawer({ taskId, onClose, onUpdate }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const isLeadOrAbove = isAdmin || !!user?.leads_any_team;
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [comment, setComment] = useState("");
    const [newCheckItem, setNewCheckItem] = useState("");
    const [newCheckAssignees, setNewCheckAssignees] = useState([]);
    const [addingCheck, setAddingCheck] = useState(false);
    const [openAssigneePicker, setOpenAssigneePicker] = useState(null); // checklist item id
    const [uploadingCheckId, setUploadingCheckId] = useState(null);
    const [activeTab, setActiveTab] = useState("comments");
    const [users, setUsers] = useState([]);
    const [showAssistance, setShowAssistance] = useState(false);
    // Templates
    const [templates, setTemplates] = useState([]);
    const [showSaveTpl, setShowSaveTpl] = useState(false);
    const [showApplyTpl, setShowApplyTpl] = useState(false);
    const [tplName, setTplName] = useState("");

    const fetchTemplates = () => {
        if (!isLeadOrAbove) return;
        API.get("/checklist-templates").then(r => setTemplates(r.data)).catch(() => {});
    };

    const fetchTask = () => {
        setLoading(true);
        API.get(`/tasks/${taskId}`)
            .then(r => setTask(r.data))
            .catch(() => toast.error("Eroare la încărcarea taskului"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTask();
        API.get("/users").then(r => setUsers(r.data)).catch(() => {});
        fetchTemplates();
    }, [taskId]);

    const update = async (field, value) => {
        setSaving(true);
        try {
            const updated = await API.patch(`/tasks/${taskId}`, { [field]: value });
            setTask(prev => ({ ...prev, ...updated.data }));
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const sendComment = async () => {
        if (!comment.trim()) return;
        try {
            await API.post(`/tasks/${taskId}/comments`, { body: comment });
            setComment("");
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const addCheckItem = async () => {
        if (!newCheckItem.trim()) return;
        try {
            await API.post(`/tasks/${taskId}/checklist`, { title: newCheckItem, assignee_ids: newCheckAssignees });
            setNewCheckItem("");
            setNewCheckAssignees([]);
            setAddingCheck(false);
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const toggleCheckAssignee = async (itemId, userId, currentList) => {
        const next = (currentList || []).includes(userId)
            ? currentList.filter(x => x !== userId)
            : [...(currentList || []), userId];
        try {
            await API.patch(`/checklist/${itemId}/assignee`, { assignee_ids: next });
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const clearCheckAssignees = async (itemId) => {
        try {
            await API.patch(`/checklist/${itemId}/assignee`, { assignee_ids: [] });
            setOpenAssigneePicker(null);
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const uploadCheckAttachment = async (itemId, file) => {
        if (!file) return;
        setUploadingCheckId(itemId);
        const formData = new FormData();
        formData.append("file", file);
        try {
            await API.post(`/checklist/${itemId}/attachment`, formData, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Fișier atașat");
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setUploadingCheckId(null);
        }
    };

    const deleteCheckAttachment = async (itemId) => {
        if (!window.confirm("Ștergi fișierul atașat acestui punct?")) return;
        try {
            await API.delete(`/checklist/${itemId}/attachment`);
            toast.success("Atașament șters");
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const toggleCheck = async (itemId, current) => {
        try {
            await API.patch(`/checklist/${itemId}?is_completed=${!current}`);
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const deleteCheck = async (itemId) => {
        try {
            await API.delete(`/checklist/${itemId}`);
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const saveAsTemplate = async (e) => {
        e.preventDefault();
        if (!tplName.trim()) return;
        try {
            await API.post("/checklist-templates/from-task", { name: tplName.trim(), task_id: taskId });
            toast.success("Șablon salvat");
            setShowSaveTpl(false);
            setTplName("");
            fetchTemplates();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const applyTemplate = async (tplId) => {
        try {
            await API.post(`/tasks/${taskId}/apply-checklist-template`, { template_id: tplId });
            toast.success("Șablon aplicat");
            setShowApplyTpl(false);
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const deleteTemplate = async (tplId) => {
        if (!window.confirm("Ștergi acest șablon?")) return;
        try {
            await API.delete(`/checklist-templates/${tplId}`);
            toast.success("Șablon șters");
            fetchTemplates();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const deleteTask = async () => {
        if (!task) return;
        const confirmText = `Ștergi PERMANENT taskul "${task.title}"?\n\nVor fi șterse și toate comentariile, checklist-urile și etichetele asociate. Această acțiune NU poate fi anulată.`;
        if (!window.confirm(confirmText)) return;
        try {
            await API.delete(`/tasks/${taskId}`);
            toast.success("Task șters");
            onClose?.();
            if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent("task-deleted", { detail: { taskId } }));
            }
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            await API.post(`/tasks/${taskId}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Fișier atașat");
            fetchTask();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const isOverdue = task?.due_date && new Date(task.due_date) < new Date() && !task?.completed_at;
    const checklistPct = task?.checklist_count > 0 ? Math.round((task.checklist_completed / task.checklist_count) * 100) : 0;

    return (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-full max-w-xl bg-white shadow-2xl border-l border-[#E4E4E7] flex flex-col z-10 overflow-hidden" data-testid="task-drawer">
                {/* Drawer Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E4E7] flex-shrink-0">
                    <div className="flex items-center gap-2">
                        {task && <span className="text-xs text-[#9CA3AF] font-mono bg-[#F1F3F5] px-2 py-0.5 rounded">#{task.task_number}</span>}
                        {saving && <Loader2 size={14} className="animate-spin text-[#002FA7]" />}
                    </div>
                    <div className="flex items-center gap-2">
                        {task && (
                            <button onClick={() => setShowAssistance(true)}
                                data-testid="task-request-assistance-btn"
                                className="flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] dark:text-[#6A91FF] border border-[#002FA7]/30 px-2.5 py-1 rounded hover:bg-[#EEF2FF] dark:hover:bg-[#1A2338] transition-colors">
                                <HandHelping size={13} /> Solicită asistență
                            </button>
                        )}
                        {task && isLeadOrAbove && (
                            <button onClick={deleteTask}
                                data-testid="task-delete-btn"
                                title="Șterge taskul"
                                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/40 px-2.5 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                <Trash2 size={13} /> Șterge
                            </button>
                        )}
                        <button onClick={onClose} data-testid="close-task-drawer" className="text-[#9CA3AF] hover:text-[#111827] transition-colors p-1 rounded hover:bg-[#F1F3F5]">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <Loader2 size={24} className="animate-spin text-[#002FA7]" />
                    </div>
                ) : task ? (
                    <div className="flex-1 overflow-y-auto px-6 py-5">
                        {/* Member read-only notice */}
                        {!isAdmin && (
                            <div data-testid="member-readonly-notice" className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-4">
                                <Lock size={12} className="text-amber-500 flex-shrink-0" />
                                <span className="text-xs text-amber-700">Poți adăuga comentarii, bifa checklist și atașa fișiere. Câmpurile Responsabil, Prioritate, Termen Limită și Descriere sunt doar pentru administratori.</span>
                            </div>
                        )}
                        {/* Title */}
                        <textarea
                            data-testid="task-title-input"
                            defaultValue={task.title}
                            onBlur={e => { if (e.target.value !== task.title) update("title", e.target.value); }}
                            className="w-full text-lg font-bold text-[#111827] resize-none focus:outline-none focus:bg-[#F8F9FA] rounded px-1 -ml-1 transition-colors"
                            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                            rows={2}
                        />
                        {isOverdue && (
                            <div className="flex items-center gap-1.5 text-red-600 bg-red-50 border border-red-100 rounded px-3 py-1.5 mt-2 mb-3">
                                <AlertCircle size={13} />
                                <span className="text-xs font-semibold">Task întârziat</span>
                            </div>
                        )}

                        {/* Meta fields */}
                        <div className="grid grid-cols-2 gap-3 mt-4 mb-5">
                            <div className="col-span-2">
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                                    Responsabili {!isAdmin && <Lock size={10} className="text-[#D1D5DB]" />}
                                </label>
                                {isAdmin ? (
                                    <AssigneeMultiSelect
                                        users={users}
                                        value={task.assignee_ids || (task.assignee?.id ? [task.assignee.id] : [])}
                                        onChange={(ids) => update("assignee_ids", ids)}
                                    />
                                ) : (
                                    <div data-testid="task-assignee-readonly" className="w-full px-2 py-1.5 border border-[#F1F3F5] rounded text-sm bg-[#F8F9FA] flex items-center gap-1.5 flex-wrap min-h-[32px]">
                                        {(task.assignees || []).length === 0 && (
                                            <span className="text-[#9CA3AF] flex items-center gap-1.5"><User size={13} /> Neatribuit</span>
                                        )}
                                        {(task.assignees || []).map(a => (
                                            <span key={a.id} className="inline-flex items-center gap-1 bg-white border border-[#E4E4E7] rounded-full px-2 py-0.5 text-xs">
                                                <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: a.avatar || "#002FA7" }}>
                                                    <span className="text-white text-[8px] font-bold">{a.name?.[0]?.toUpperCase()}</span>
                                                </span>
                                                {a.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                                    Prioritate {!isAdmin && <Lock size={10} className="text-[#D1D5DB]" />}
                                </label>
                                {isAdmin ? (
                                    <select
                                        data-testid="task-priority-select"
                                        value={task.priority || "medium"}
                                        onChange={e => update("priority", e.target.value)}
                                        className="w-full px-2 py-1.5 border border-[#E4E4E7] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white"
                                    >
                                        {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                ) : (
                                    <div data-testid="task-priority-readonly" className="w-full px-2 py-1.5 border border-[#F1F3F5] rounded text-sm bg-[#F8F9FA] flex items-center gap-1.5">
                                        {(() => {
                                            const p = PRIORITY_OPTIONS.find(o => o.value === (task.priority || "medium"));
                                            return <>
                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p?.color }} />
                                                <span style={{ color: p?.color }} className="font-medium">{p?.label || "Mediu"}</span>
                                            </>;
                                        })()}
                                    </div>
                                )}
                            </div>
                            <div className="col-span-2">
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                                    Termen limită {!isAdmin && <Lock size={10} className="text-[#D1D5DB]" />}
                                </label>
                                {isAdmin ? (
                                    <input
                                        data-testid="task-due-date-input"
                                        type="date"
                                        value={task.due_date ? task.due_date.split("T")[0] : ""}
                                        onChange={e => update("due_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                                        className="w-full px-2 py-1.5 border border-[#E4E4E7] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                                    />
                                ) : (
                                    <div data-testid="task-due-date-readonly" className="w-full px-2 py-1.5 border border-[#F1F3F5] rounded text-sm bg-[#F8F9FA] text-[#6B7280] flex items-center gap-1.5">
                                        <Calendar size={13} className="text-[#D1D5DB] flex-shrink-0" />
                                        <span>{task.due_date ? new Date(task.due_date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "Nefixat"}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <Section title="Descriere" icon={<Flag size={14} />}>
                            {isAdmin ? (
                                <textarea
                                    data-testid="task-description-input"
                                    defaultValue={task.description || ""}
                                    onBlur={e => { if (e.target.value !== (task.description || "")) update("description", e.target.value); }}
                                    placeholder="Adaugă o descriere detaliată..."
                                    className="w-full text-sm text-[#4B5563] resize-none focus:outline-none focus:bg-[#F8F9FA] rounded px-2 py-1.5 -ml-2 transition-colors min-h-[80px]"
                                    rows={4}
                                />
                            ) : (
                                <div data-testid="task-description-readonly" className="w-full text-sm text-[#4B5563] rounded px-2 py-1.5 -ml-2 min-h-[60px] bg-[#F8F9FA] border border-[#F1F3F5] flex items-start gap-1.5">
                                    <Lock size={12} className="text-[#D1D5DB] flex-shrink-0 mt-0.5" />
                                    <span className={task.description ? "" : "text-[#C4C9D4] italic"}>
                                        {task.description || "Fără descriere"}
                                    </span>
                                </div>
                            )}
                        </Section>

                        {/* Checklist */}
                        {(task.checklist?.length > 0 || addingCheck) && (
                            <Section title={`Checklist ${task.checklist?.length > 0 ? `(${checklistPct}%)` : ""}`} icon={<CheckSquare size={14} />}>
                                {/* Template toolbar (lead/admin only) */}
                                {isLeadOrAbove && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <button type="button" onClick={() => setShowApplyTpl(true)}
                                            data-testid="apply-template-btn"
                                            className="flex items-center gap-1 text-[11px] font-semibold text-[#002FA7] dark:text-[#6A91FF] border border-[#002FA7]/30 px-2 py-1 rounded hover:bg-[#EEF2FF] dark:hover:bg-[#1A2338] transition-colors">
                                            <FolderOpen size={11} /> Încarcă șablon
                                        </button>
                                        {task.checklist?.length > 0 && (
                                            <button type="button" onClick={() => setShowSaveTpl(true)}
                                                data-testid="save-template-btn"
                                                className="flex items-center gap-1 text-[11px] font-semibold text-[#6B7280] border border-[#E4E4E7] dark:border-[#252B33] px-2 py-1 rounded hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-colors">
                                                <BookmarkPlus size={11} /> Salvează ca șablon
                                            </button>
                                        )}
                                    </div>
                                )}
                                {task.checklist?.length > 0 && (
                                    <div className="mb-2">
                                        <div className="h-1 bg-[#E4E4E7] rounded-full overflow-hidden">
                                            <div className="h-full bg-[#10B981] rounded-full transition-all duration-300" style={{ width: `${checklistPct}%` }} />
                                        </div>
                                    </div>
                                )}
                                <div className="space-y-1.5">
                                    {task.checklist?.map(item => {
                                        const itemAssigneeIds = item.assignee_ids || (item.assignee_id ? [item.assignee_id] : []);
                                        const itemAssignees = itemAssigneeIds.map(aid => users.find(u => u.id === aid)).filter(Boolean);
                                        const taskAssignees = users.filter(u => (task.assignee_ids || []).includes(u.id));
                                        const isUploading = uploadingCheckId === item.id;
                                        return (
                                            <div key={item.id} className="flex items-center gap-2 group relative">
                                                <button onClick={() => toggleCheck(item.id, item.is_completed)} className="flex-shrink-0">
                                                    <div className={`w-4 h-4 rounded border transition-colors flex items-center justify-center
                                                        ${item.is_completed ? "bg-[#10B981] border-[#10B981]" : "border-[#D1D5DB] hover:border-[#002FA7]"}`}>
                                                        {item.is_completed && <Check size={11} className="text-white" />}
                                                    </div>
                                                </button>
                                                <span className={`flex-1 min-w-0 text-sm transition-colors truncate ${item.is_completed ? "line-through text-[#9CA3AF]" : "text-[#111827] dark:text-[#F3F4F6]"}`}>
                                                    {item.title}
                                                </span>
                                                {/* Attachment indicator / download */}
                                                {item.attachment && (
                                                    <a
                                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/checklist/${item.id}/attachment`}
                                                        target="_blank" rel="noreferrer"
                                                        title={`${item.attachment.file_name} (${Math.round((item.attachment.size || 0) / 1024)} KB)`}
                                                        data-testid={`check-attach-link-${item.id}`}
                                                        className="flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] text-[#002FA7] hover:underline dark:text-[#6A91FF] max-w-[80px] truncate"
                                                    >
                                                        <Paperclip size={11} />
                                                        <span className="truncate">{item.attachment.file_name}</span>
                                                    </a>
                                                )}
                                                {/* Upload / replace / delete attachment */}
                                                {isLeadOrAbove && (
                                                    <>
                                                        {!item.attachment ? (
                                                            <label
                                                                className={`flex-shrink-0 cursor-pointer text-[#9CA3AF] hover:text-[#002FA7] p-0.5 rounded hover:bg-[#EEF2FF] dark:hover:bg-[#1A2338] transition-colors ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
                                                                title="Atașează un fișier"
                                                                data-testid={`check-attach-btn-${item.id}`}
                                                            >
                                                                {isUploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                                                                <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) { uploadCheckAttachment(item.id, e.target.files[0]); e.target.value = ""; } }} />
                                                            </label>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => deleteCheckAttachment(item.id)}
                                                                title="Șterge atașamentul"
                                                                data-testid={`check-attach-del-${item.id}`}
                                                                className="flex-shrink-0 text-[#9CA3AF] hover:text-red-500 p-0.5 rounded transition-colors"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {/* Assignee avatars / picker */}
                                                {isLeadOrAbove ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`check-assignee-btn-${item.id}`}
                                                        onClick={() => setOpenAssigneePicker(openAssigneePicker === item.id ? null : item.id)}
                                                        title={itemAssignees.length > 0 ? `Atribuit la ${itemAssignees.map(u => u.name).join(", ")}` : "Atribuie responsabili"}
                                                        className="flex-shrink-0"
                                                    >
                                                        {itemAssignees.length > 0 ? (
                                                            <div className="flex items-center -space-x-1.5">
                                                                {itemAssignees.slice(0, 3).map(u => (
                                                                    <div key={u.id} className="w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#151A21] hover:ring-[#002FA7] transition-all" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                                        <span className="text-white text-[9px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                                                    </div>
                                                                ))}
                                                                {itemAssignees.length > 3 && (
                                                                    <div className="w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#151A21] bg-[#6B7280]">
                                                                        <span className="text-white text-[8px] font-bold">+{itemAssignees.length - 3}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="w-5 h-5 rounded-full border border-dashed border-[#D1D5DB] hover:border-[#002FA7] hover:text-[#002FA7] text-[#9CA3AF] flex items-center justify-center transition-colors">
                                                                <User size={10} />
                                                            </div>
                                                        )}
                                                    </button>
                                                ) : itemAssignees.length > 0 ? (
                                                    <div className="flex items-center -space-x-1.5 flex-shrink-0">
                                                        {itemAssignees.slice(0, 3).map(u => (
                                                            <div key={u.id} className="w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#151A21]" title={u.name} style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                                <span className="text-white text-[9px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : null}
                                                {/* Assignee picker popover (multi-select) */}
                                                {isLeadOrAbove && openAssigneePicker === item.id && (
                                                    <>
                                                        <div className="fixed inset-0 z-40" onClick={() => setOpenAssigneePicker(null)} />
                                                        <div data-testid={`check-assignee-popover-${item.id}`}
                                                            className="absolute right-6 top-6 z-50 bg-white dark:bg-[#1C222A] border border-[#E4E4E7] dark:border-[#252B33] rounded-md shadow-lg w-60 py-1 max-h-72 overflow-y-auto">
                                                            <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-[#9CA3AF] tracking-wider border-b border-[#F1F3F5] dark:border-[#252B33] flex items-center justify-between">
                                                                <span>Atribuie ({itemAssigneeIds.length})</span>
                                                                <button type="button" onClick={() => setOpenAssigneePicker(null)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F3F4F6]">
                                                                    <X size={11} />
                                                                </button>
                                                            </div>
                                                            {taskAssignees.length === 0 && (
                                                                <div className="px-3 py-2 text-xs text-[#9CA3AF]">Adaugă mai întâi responsabili la task.</div>
                                                            )}
                                                            {taskAssignees.map(u => {
                                                                const checked = itemAssigneeIds.includes(u.id);
                                                                return (
                                                                    <button key={u.id} type="button"
                                                                        onClick={() => toggleCheckAssignee(item.id, u.id, itemAssigneeIds)}
                                                                        data-testid={`check-assign-option-${item.id}-${u.id}`}
                                                                        className={`w-full flex items-center gap-2 text-xs px-3 py-1.5 text-left hover:bg-[#F8F9FA] dark:hover:bg-[#252B33] ${checked ? "bg-[#EEF2FF] dark:bg-[#1A2338]" : ""}`}>
                                                                        <input type="checkbox" checked={checked} readOnly className="w-3.5 h-3.5 accent-[#002FA7] pointer-events-none" />
                                                                        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                                            <span className="text-white text-[9px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                                                        </div>
                                                                        <span className="flex-1 truncate text-[#111827] dark:text-[#F3F4F6]">{u.name}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                            {itemAssigneeIds.length > 0 && (
                                                                <>
                                                                    <div className="border-t border-[#F1F3F5] dark:border-[#252B33] my-0.5" />
                                                                    <button type="button"
                                                                        onClick={() => clearCheckAssignees(item.id)}
                                                                        data-testid={`check-assign-clear-${item.id}`}
                                                                        className="w-full flex items-center gap-2 text-xs px-3 py-1.5 text-left text-[#EF4444] hover:bg-[#FEF2F2] dark:hover:bg-[#3a1c1c]">
                                                                        <X size={11} /> Elimină toți responsabilii
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                <button onClick={() => deleteCheck(item.id)} className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-red-500 transition-all flex-shrink-0">
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                {addingCheck ? (
                                    <div className="mt-2 flex flex-col gap-1.5">
                                        <div className="flex gap-2">
                                            <input
                                                autoFocus
                                                value={newCheckItem}
                                                onChange={e => setNewCheckItem(e.target.value)}
                                                onKeyDown={e => { if (e.key === "Enter") addCheckItem(); if (e.key === "Escape") { setAddingCheck(false); setNewCheckAssignees([]); } }}
                                                placeholder="Adaugă punct..."
                                                className="flex-1 px-2 py-1 border border-[#E4E4E7] rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                                            />
                                            <button onClick={addCheckItem} data-testid="add-check-submit" className="text-xs bg-[#002FA7] text-white px-3 py-1 rounded hover:bg-[#002482] transition-colors">Adaugă</button>
                                            <button onClick={() => { setAddingCheck(false); setNewCheckAssignees([]); }} className="text-xs text-[#9CA3AF] hover:text-[#111827]">Anulează</button>
                                        </div>
                                        {(() => {
                                            const taskAssignees = users.filter(u => (task.assignee_ids || []).includes(u.id));
                                            if (taskAssignees.length === 0) return null;
                                            return (
                                                <div className="flex items-center gap-1.5 flex-wrap pl-1">
                                                    <span className="text-[10px] uppercase font-bold text-[#9CA3AF] tracking-wider">Atribuie:</span>
                                                    {taskAssignees.map(u => {
                                                        const checked = newCheckAssignees.includes(u.id);
                                                        return (
                                                            <button key={u.id} type="button"
                                                                onClick={() => setNewCheckAssignees(checked ? newCheckAssignees.filter(x => x !== u.id) : [...newCheckAssignees, u.id])}
                                                                data-testid={`new-check-assignee-${u.id}`}
                                                                className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${checked ? "bg-[#EEF2FF] border-[#002FA7] text-[#002FA7]" : "border-[#E4E4E7] text-[#6B7280] hover:bg-[#F8F9FA]"}`}>
                                                                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                                    <span className="text-white text-[7px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                                                </div>
                                                                {u.name}
                                                                {checked && <Check size={9} />}
                                                            </button>
                                                        );
                                                    })}
                                                    {newCheckAssignees.length > 0 && (
                                                        <button type="button" onClick={() => setNewCheckAssignees([])}
                                                            data-testid="new-check-assignee-clear"
                                                            className="text-[10px] text-[#6B7280] hover:text-[#111827] underline ml-1">
                                                            Resetează
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : isLeadOrAbove ? (
                                    <button onClick={() => setAddingCheck(true)} className="flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-[#002FA7] mt-2 transition-colors">
                                        <Plus size={13} /> Adaugă punct
                                    </button>
                                ) : null}
                            </Section>
                        )}

                        {/* Add checklist item button if no checklist yet (lead/admin only) */}
                        {task.checklist?.length === 0 && !addingCheck && isLeadOrAbove && (
                            <div className="mb-5 flex flex-col gap-2">
                                <button onClick={() => setAddingCheck(true)} data-testid="add-checklist-btn" className="flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#002FA7] border border-dashed border-[#E4E4E7] dark:border-[#252B33] hover:border-[#002FA7] px-3 py-2 rounded-md w-full justify-center transition-colors">
                                    <CheckSquare size={13} /> Adaugă checklist
                                </button>
                                {templates.length > 0 && (
                                    <button onClick={() => setShowApplyTpl(true)} data-testid="apply-template-empty-btn"
                                        className="flex items-center gap-1.5 text-xs text-[#002FA7] dark:text-[#6A91FF] hover:bg-[#EEF2FF] dark:hover:bg-[#1A2338] border border-dashed border-[#002FA7]/30 px-3 py-2 rounded-md w-full justify-center transition-colors">
                                        <FolderOpen size={13} /> Folosește un șablon
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="flex gap-0 border-b border-[#E4E4E7] mb-4">
                            {[
                                { id: "comments", label: `Comentarii (${task.comments?.length || 0})`, icon: <MessageCircle size={13} /> },
                                { id: "attachments", label: `Atașamente (${task.attachments?.length || 0})`, icon: <Paperclip size={13} /> },
                                { id: "activity", label: "Activitate", icon: <Tag size={13} /> },
                            ].map(tab => (
                                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                    data-testid={`tab-${tab.id}`}
                                    className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors
                                        ${activeTab === tab.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#9CA3AF] hover:text-[#4B5563]"}`}>
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Comments Tab */}
                        {activeTab === "comments" && (
                            <div>
                                <div className="space-y-3 mb-4">
                                    {(task.comments || []).map(c => (
                                        <div key={c.id} className="flex gap-2.5">
                                            <div className="w-7 h-7 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-xs font-bold">{c.author?.name?.[0] || "?"}</span>
                                            </div>
                                            <div className="flex-1 bg-[#F8F9FA] rounded-md px-3 py-2">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-semibold text-[#111827]">{c.author?.name}</span>
                                                    <span className="text-xs text-[#9CA3AF]">
                                                        {new Date(c.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-[#4B5563] whitespace-pre-wrap">{c.body}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <div className="w-7 h-7 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white text-xs font-bold">{user?.name?.[0] || "U"}</span>
                                    </div>
                                    <div className="flex-1 flex gap-2">
                                        <textarea
                                            data-testid="comment-input"
                                            value={comment}
                                            onChange={e => setComment(e.target.value)}
                                            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendComment(); }}
                                            placeholder="Scrie un comentariu... (Ctrl+Enter pentru a trimite)"
                                            rows={2}
                                            className="flex-1 px-3 py-2 border border-[#E4E4E7] rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                                        />
                                        <button
                                            data-testid="send-comment-btn"
                                            onClick={sendComment}
                                            disabled={!comment.trim()}
                                            className="self-end bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] disabled:opacity-40 transition-colors"
                                        >
                                            <Send size={15} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Attachments Tab */}
                        {activeTab === "attachments" && (
                            <div>
                                <div className="space-y-2 mb-4">
                                    {(task.attachments || []).map(a => (
                                        <div key={a.id} className="flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-md hover:bg-[#F8F9FA] transition-colors">
                                            <Paperclip size={16} className="text-[#9CA3AF] flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-[#111827] truncate">{a.file_name}</p>
                                                <p className="text-xs text-[#9CA3AF]">{(a.size / 1024).toFixed(1)} KB</p>
                                            </div>
                                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}`} target="_blank" rel="noreferrer"
                                                className="text-xs text-[#002FA7] hover:underline font-semibold" data-testid={`download-attach-${a.id}`}>
                                                Descarcă
                                            </a>
                                        </div>
                                    ))}
                                    {(task.attachments || []).length === 0 && (
                                        <p className="text-sm text-[#9CA3AF] text-center py-4">Niciun atașament</p>
                                    )}
                                </div>
                                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#E4E4E7] hover:border-[#002FA7] rounded-md p-4 cursor-pointer transition-colors group">
                                    <Paperclip size={16} className="text-[#9CA3AF] group-hover:text-[#002FA7]" />
                                    <span className="text-sm text-[#9CA3AF] group-hover:text-[#002FA7]">Atașează fișier</span>
                                    <input data-testid="file-upload-input" type="file" className="hidden" onChange={handleFileUpload} />
                                </label>
                            </div>
                        )}

                        {/* Activity Tab */}
                        {activeTab === "activity" && (
                            <div className="space-y-3">
                                {(task.activity || []).map(a => (
                                    <div key={a.id} className="flex items-start gap-2.5">
                                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <span className="text-xs text-gray-500 font-semibold">{a.actor?.name?.[0] || "?"}</span>
                                        </div>
                                        <div>
                                            <p className="text-xs text-[#4B5563]">
                                                <span className="font-semibold">{a.actor?.name}</span> {a.action.replace(".", " ")}
                                                {a.metadata?.from && ` din "${a.metadata.from}" în "${a.metadata.to}"`}
                                            </p>
                                            <p className="text-xs text-[#9CA3AF] mt-0.5">
                                                {new Date(a.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {(task.activity || []).length === 0 && (
                                    <p className="text-sm text-[#9CA3AF] text-center py-4">Nicio activitate</p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-[#9CA3AF] text-sm">Task negăsit</div>
                )}
            </div>
            {showAssistance && task && (
                <AssistanceModal
                    taskId={taskId}
                    task={task}
                    onClose={() => setShowAssistance(false)}
                    onSuccess={() => {}}
                />
            )}

            {/* Save checklist as template modal */}
            {showSaveTpl && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="save-tpl-modal">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowSaveTpl(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-md shadow-2xl w-full max-w-sm mx-4 z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                        <form onSubmit={saveAsTemplate} className="p-5 space-y-4">
                            <div>
                                <h3 className="font-bold text-[#111827] dark:text-[#F3F4F6] mb-1">Salvează checklist ca șablon</h3>
                                <p className="text-xs text-[#9CA3AF]">Șabloanele sunt personale și le poți folosi pe orice task.</p>
                            </div>
                            <input
                                autoFocus value={tplName} onChange={e => setTplName(e.target.value)}
                                placeholder="Nume șablon..."
                                data-testid="tpl-name-input"
                                className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                                required />
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowSaveTpl(false)} className="flex-1 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm text-[#4B5563] dark:text-[#9CA3AF] hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A]">Anulează</button>
                                <button type="submit" data-testid="tpl-save-submit" className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482]">Salvează</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Apply template modal */}
            {showApplyTpl && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="apply-tpl-modal">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowApplyTpl(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-md shadow-2xl w-full max-w-md mx-4 z-10 border border-[#E4E4E7] dark:border-[#252B33]">
                        <div className="px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33] flex items-center justify-between">
                            <h3 className="font-bold text-[#111827] dark:text-[#F3F4F6]">Șabloanele mele</h3>
                            <button onClick={() => setShowApplyTpl(false)} className="text-[#9CA3AF] hover:text-[#111827] dark:hover:text-white"><X size={16} /></button>
                        </div>
                        <div className="p-4 max-h-80 overflow-y-auto">
                            {templates.length === 0 ? (
                                <p className="text-sm text-[#9CA3AF] text-center py-6">Nu ai niciun șablon salvat. Creează unul dintr-un checklist existent.</p>
                            ) : (
                                <div className="space-y-1.5">
                                    {templates.map(t => (
                                        <div key={t.id} className="flex items-center gap-2 group border border-[#E4E4E7] dark:border-[#252B33] rounded-md px-3 py-2 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-colors">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{t.name}</p>
                                                <p className="text-xs text-[#9CA3AF]">{t.items?.length || 0} puncte</p>
                                            </div>
                                            <button onClick={() => applyTemplate(t.id)} data-testid={`tpl-apply-${t.id}`}
                                                className="text-xs font-semibold text-[#002FA7] dark:text-[#6A91FF] hover:bg-[#EEF2FF] dark:hover:bg-[#1A2338] px-2 py-1 rounded">
                                                Folosește
                                            </button>
                                            <button onClick={() => deleteTemplate(t.id)} data-testid={`tpl-delete-${t.id}`}
                                                className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-red-500 transition-all p-1">
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function AssigneeMultiSelect({ users, value, onChange }) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selected = users.filter(u => value.includes(u.id));
    const filtered = users.filter(u =>
        !value.includes(u.id) &&
        (u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
    );

    const toggle = (id) => {
        if (value.includes(id)) onChange(value.filter(x => x !== id));
        else onChange([...value, id]);
    };

    return (
        <div ref={ref} className="relative" data-testid="task-assignee-multi">
            <div onClick={() => setOpen(true)}
                className="w-full px-2 py-1.5 border border-[#E4E4E7] dark:border-[#252B33] rounded text-sm bg-white dark:bg-[#0F1317] min-h-[34px] cursor-text flex items-center gap-1 flex-wrap focus-within:ring-1 focus-within:ring-[#002FA7]">
                {selected.length === 0 && <span className="text-[#9CA3AF]">Alege unul sau mai mulți...</span>}
                {selected.map(u => (
                    <span key={u.id} className="inline-flex items-center gap-1 bg-blue-50 dark:bg-[#1A2338] text-[#002FA7] dark:text-[#6A91FF] rounded-full pl-1 pr-1.5 py-0.5 text-xs">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                            <span className="text-white text-[8px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                        </span>
                        {u.name}
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggle(u.id); }} className="ml-0.5 hover:text-red-500">×</button>
                    </span>
                ))}
            </div>
            {open && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Caută persoană..."
                        className="w-full px-3 py-2 text-sm border-b border-[#E4E4E7] dark:border-[#252B33] focus:outline-none dark:bg-[#0F1317] dark:text-[#F3F4F6]" />
                    {filtered.length === 0 ? (
                        <p className="text-xs text-[#9CA3AF] p-3 text-center">Niciun utilizator.</p>
                    ) : (
                        filtered.map(u => (
                            <button key={u.id} type="button" onClick={() => { toggle(u.id); setSearch(""); }}
                                data-testid={`assignee-option-${u.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] text-left">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                    <span className="text-white text-[10px] font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{u.name}</p>
                                    <p className="text-[10px] text-[#9CA3AF] truncate">{u.email}</p>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
